import React, { useContext, useRef, useState, useEffect, useCallback } from "react";
import {
  Form,
  Input,
  Button,
  Select,
  DatePicker,
  Slider,
  Checkbox,
  Alert,
  Row,
  Col,
  message,
} from "antd";
import {
  CloseOutlined,
  CloudUploadOutlined,
  ScissorOutlined,
  SearchOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import FacePhotoCropModal from "./FacePhotoCropModal";
import { connect } from "react-redux";
import { faceSearchAction } from "../../_actions/face.search.service";
import {
  FAILED_STATUSES,
  getExternalResultStatus,
  isFaceSearchComplete,
} from "./faceSearchResults.utils";
import { FaceSearchAiLoader } from "./FaceSearchAiLoader";
import {
  AI_THEME,
  AiHero,
  AiSection,
  AiStyles,
  aiPageShellStyle,
  aiPrimaryButtonStyle,
  aiSecondaryButtonStyle,
} from "./faceSearchTheme";
import { LanguageContext } from "../../context/LanguageContext";

const { Option } = Select;

const SEARCH_STAGES = [
  "Uploading photo securely...",
  "Initializing AI face engine...",
  "Detecting facial landmarks...",
  "Generating face embeddings...",
  "Searching criminal database...",
  "Ranking similarity matches...",
];

const PHOTO_SOURCE_OPTIONS = ["CCTV", "रेकॉर्ड वरील", "इतर"];
const ALLOWED_IMAGE_TYPES = ["image/jpeg", "image/png"];
const ALLOWED_IMAGE_EXTENSIONS = [".jpg", ".jpeg", ".png"];
const ALLOWED_FORMATS_LABEL = "PNG, JPG, JPEG";
const MAX_PHOTOS = 1;
const TOP_RESULTS = 10;
const RESULT_POLL_DELAY_MS = 20000;
const MAX_POLL_ATTEMPTS = 30;

const getFileExtension = (fileName = "") => {
  const dotIndex = fileName.lastIndexOf(".");
  return dotIndex >= 0 ? fileName.slice(dotIndex).toLowerCase() : "";
};

const isAllowedImageFile = (file) => {
  const extension = getFileExtension(file.name);
  if (!ALLOWED_IMAGE_EXTENSIONS.includes(extension)) {
    return false;
  }

  if (file.type && !ALLOWED_IMAGE_TYPES.includes(file.type.toLowerCase())) {
    return false;
  }

  return true;
};

const pollFaceSearchResult = async (taskId, onStageChange) => {
  onStageChange?.(SEARCH_STAGES[1]);

  for (let attempt = 0; attempt < MAX_POLL_ATTEMPTS; attempt += 1) {
    onStageChange?.(
      SEARCH_STAGES[Math.min(attempt + 2, SEARCH_STAGES.length - 1)],
    );

    await new Promise((resolve) => setTimeout(resolve, RESULT_POLL_DELAY_MS));

    const resultResponse = await faceSearchAction.getFindFaceSearchId(taskId);
    const externalStatus = getExternalResultStatus(resultResponse);

    if (FAILED_STATUSES.includes(externalStatus)) {
      throw new Error("Face search failed");
    }

    if (isFaceSearchComplete(resultResponse)) {
      return resultResponse;
    }
  }

  throw new Error("Face search timed out. Please try again later.");
};

const formItemLayout = {
  labelCol: {
    xs: { span: 24 },
    sm: { span: 10 },
    md: { span: 9 },
    lg: { span: 8 },
  },
  wrapperCol: {
    xs: { span: 24 },
    sm: { span: 14 },
    md: { span: 15 },
    lg: { span: 16 },
  },
  labelAlign: "left",
};

const fullWidthItemLayout = {
  labelCol: {
    xs: { span: 24 },
    sm: { span: 5 },
    md: { span: 4 },
    lg: { span: 3 },
  },
  wrapperCol: {
    xs: { span: 24 },
    sm: { span: 19 },
    md: { span: 20 },
    lg: { span: 21 },
  },
  labelAlign: "left",
};

const FindFaceSearchFormComponent = ({
  user,
  onSubmit,
  onReset,
  onLoadingChange,
  compact = false,
  sidebar = false,
  visibleSection = null,
  externalLoader = false,
  district,
}) => {
  console.log("district", district);
  const [form] = Form.useForm();
  const fileInputRef = useRef(null);
  const cropFileInputRef = useRef(null);
  const [photoList, setPhotoList] = useState([]);
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [cropImageSrc, setCropImageSrc] = useState("");
  const [pendingCropFile, setPendingCropFile] = useState(null);
  const [accuracy, setAccuracy] = useState(70);
  const [loading, setLoading] = useState(false);
  const [searchStage, setSearchStage] = useState(SEARCH_STAGES[0]);
  const [searchComplete, setSearchComplete] = useState(false);

   const { language, setLanguage, t } = useContext(LanguageContext);

  const setRole2DistrictValue = useCallback(() => {
    if (user?.role !== "2" || !district?.length) return;

    const matched =
      district.find((d) => String(d.id) === String(user.districtId)) ||
      district[0];

    if (matched) {
      form.setFieldsValue({ district: matched.id });
    }
  }, [user?.role, user?.districtId, district, form]);

  useEffect(() => {
    setRole2DistrictValue();
  }, [setRole2DistrictValue]);

  const handleChooseFiles = () => fileInputRef.current?.click();
  const handleChooseCropFile = () => cropFileInputRef.current?.click();

  const addPhotoToList = (file) => {
    const photo = {
      uid: `${Date.now()}-${file.name}-${Math.random()}`,
      name: file.name,
      file,
      preview: URL.createObjectURL(file),
    };
    setPhotoList((prev) => [...prev, photo]);
  };

  const handleFileChange = (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;

    const remainingSlots = MAX_PHOTOS - photoList.length;
    if (remainingSlots <= 0) {
      message.warning(`Maximum ${MAX_PHOTOS} photos allowed`);
      event.target.value = "";
      return;
    }

    const validFiles = [];
    files.slice(0, remainingSlots).forEach((file) => {
      if (!isAllowedImageFile(file)) {
        message.error(
          `${file.name}: Only ${ALLOWED_FORMATS_LABEL} files are allowed`,
        );
        return;
      }
      validFiles.push(file);
    });

    if (validFiles.length) addPhotoToList(validFiles[0]);
    event.target.value = "";
  };

  const handleCropFileChange = (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    if (photoList.length >= MAX_PHOTOS) {
      message.warning(`Maximum ${MAX_PHOTOS} photos allowed`);
      return;
    }

    if (!isAllowedImageFile(file)) {
      message.error(`Only ${ALLOWED_FORMATS_LABEL} files are allowed`);
      return;
    }

    setPendingCropFile(file);
    setCropImageSrc(URL.createObjectURL(file));
    setCropModalOpen(true);
  };

  const closeCropModal = () => {
    if (cropImageSrc) URL.revokeObjectURL(cropImageSrc);
    setCropModalOpen(false);
    setCropImageSrc("");
    setPendingCropFile(null);
  };

  const handleCropApply = (croppedFile) => {
    if (photoList.length >= MAX_PHOTOS) {
      message.warning(`Maximum ${MAX_PHOTOS} photos allowed`);
      closeCropModal();
      return;
    }
    addPhotoToList(croppedFile);
    closeCropModal();
  };

  const handleUseOriginalPhoto = () => {
    if (!pendingCropFile) return;
    if (photoList.length >= MAX_PHOTOS) {
      message.warning(`Maximum ${MAX_PHOTOS} photos allowed`);
      closeCropModal();
      return;
    }
    addPhotoToList(pendingCropFile);
    closeCropModal();
  };

  const handleRemovePhoto = (uid) => {
    setPhotoList((prev) => {
      const photo = prev.find((item) => item.uid === uid);
      if (photo) URL.revokeObjectURL(photo.preview);
      return prev.filter((item) => item.uid !== uid);
    });
  };

  const handleReset = () => {
    form.resetFields();
    setRole2DistrictValue();
    photoList.forEach((photo) => URL.revokeObjectURL(photo.preview));
    setPhotoList([]);
    setAccuracy(70);
    setSearchStage(SEARCH_STAGES[0]);
    setSearchComplete(false);
    onReset?.();
  };

  const onFinish = async (values) => {
    if (!photoList.length) {
      message.error("Please upload at least one photo");
      return;
    }

    const invalidPhoto = photoList.find(
      (photo) => !isAllowedImageFile(photo.file),
    );
    if (invalidPhoto) {
      message.error(`Only ${ALLOWED_FORMATS_LABEL} files are allowed`);
      return;
    }

    setLoading(true);
    onLoadingChange?.(true, photoList[0]?.preview || "", SEARCH_STAGES[0]);
    setSearchComplete(false);
    setSearchStage(SEARCH_STAGES[0]);

    try {
      const threshold = Number((accuracy / 100).toFixed(1));
      const requestData = {
        top: TOP_RESULTS,
        threshold,
        name: values.officerName,
        cityId: user && user.cityId,
        crNo: values.crNo,
        dateTime: values.filingDate
          ? values.filingDate.format("YYYY-MM-DD")
          : null,
        remarks: values.remarks || "",
        reason: values.remarks || values.photoSource || "",
        photo_source: values.photoSource,
        mobile_number: values.officerNumber,
        userid: user && user.id,
        image_file: photoList[0].file,
        device_id: "Web",
        district: values.district,
      };

      const createResponse =
        await faceSearchAction.createFindFaceSearch(requestData);
      const taskId =
        createResponse.id ||
        createResponse.taskId ||
        createResponse.data?.id ||
        createResponse.data?.task_id ||
        createResponse.data?.taskId;

      if (!taskId) {
        message.error("Task ID not received from server");
        return;
      }

      setSearchStage(SEARCH_STAGES[1]);
      onLoadingChange?.(true, photoList[0]?.preview || "", SEARCH_STAGES[1]);
      const resultResponse = await pollFaceSearchResult(taskId, (stage) => {
        setSearchStage(stage);
        onLoadingChange?.(true, photoList[0]?.preview || "", stage);
      });

      setSearchComplete(true);
      setSearchStage("Analysis complete");
      onLoadingChange?.(
        true,
        photoList[0]?.preview || "",
        "Analysis complete",
        true,
      );
      await new Promise((resolve) => setTimeout(resolve, 900));

      onSubmit?.({
        taskId,
        requestData,
        result: resultResponse,
        inputImageUrl: photoList[0]?.preview || "",
      });
    } catch (error) {
      console.error("Face search error:", error);
      message.error(
        typeof error === "string" ? error : "Failed to submit face search",
      );
    } finally {
      setLoading(false);
      setSearchComplete(false);
      setSearchStage(SEARCH_STAGES[0]);
      onLoadingChange?.(false);
    }
  };

  if (loading && !compact && !externalLoader) {
    return (
      <FaceSearchAiLoader
        imageUrl={photoList[0]?.preview || ""}
        stage={searchStage}
        complete={searchComplete}
      />
    );
  }

  const formDisabled = loading && externalLoader;
  const isNarrowLayout = compact || sidebar;
  const showCrimeInfo = !visibleSection || visibleSection === "fields";
  const showOfficerInfo = !visibleSection || visibleSection === "fields";
  const showPhotoInfo = !visibleSection || visibleSection === "upload";
  const showAccuracy =
    !visibleSection ||
    visibleSection === "fields" ||
    visibleSection === "upload";

  const sectionVisibility = (visible) =>
    visibleSection && compact
      ? { display: visible ? "block" : "none" }
      : visible
        ? {}
        : { display: "none" };

  const sectionLayout = isNarrowLayout
    ? {
        labelCol: { span: 24 },
        wrapperCol: { span: 24 },
        labelAlign: "left",
      }
    : formItemLayout;

  const fullWidthLayout = isNarrowLayout
    ? {
        labelCol: { span: 24 },
        wrapperCol: { span: 24 },
        labelAlign: "left",
      }
    : fullWidthItemLayout;

  const fieldColSpan = isNarrowLayout ? 24 : 12;

  return (
    <div
      className={sidebar ? "face-search-form-sidebar" : undefined}
      style={{
        ...aiPageShellStyle,
        padding: compact || sidebar ? 0 : aiPageShellStyle.padding,
        position: "relative",
        opacity: formDisabled ? 0.72 : 1,
        transition: "opacity 0.2s ease",
      }}
    >
      <AiStyles />
      {formDisabled && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            zIndex: 3,
            cursor: "not-allowed",
            background: "rgba(255, 255, 255, 0.35)",
            borderRadius: AI_THEME.radius,
          }}
        />
      )}

      <Form
        form={form}
        layout={isNarrowLayout ? "vertical" : "horizontal"}
        {...sectionLayout}
        onFinish={onFinish}
        colon={!isNarrowLayout}
        requiredMark={false}
        initialValues={{ accuracy: 70, photoConfirmation: false }}
      >
        <div style={sectionVisibility(showCrimeInfo)}>
          <AiSection
            step={compact ? null : "1"}
            title="गुन्हा माहिती"
            compact={compact || sidebar}
          >
            <Row gutter={[isNarrowLayout ? 0 : 24, 12]}>
              <Col xs={24} lg={fieldColSpan}>
                {user.role === "4" && (
                  <Form.Item
                    label={t("police_stations")}
                    name="district"
                    rules={[
                      { required: true, message: "Please select district" },
                    ]}
                  >
                    <Select
                      placeholder="Select district"
                      size="large"
                      disabled={formDisabled}
                    >
                      {district?.map((dis) => (
                        <Option key={dis.id} value={dis.id}>
                          {dis.DistrictName}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                )}

                {user.role == "2" && (
                  <Form.Item
                    name="district"
                    label={t("police_stations")}
                    rules={[
                      { required: true, message: "Please select district" },
                    ]}
                  >
                    <Select
                      placeholder="Select district"
                      size="large"
                      disabled
                    >
                      {district?.map((dis) => (
                        <Option key={dis.id} value={dis.id}>
                          {dis.DistrictName}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                )}
              </Col>
              <Col xs={24} lg={fieldColSpan}>
                <Form.Item
                  label={t("cr.no")}
                  name="crNo"
                  rules={[
                    { required: true, message: "Please enter CR No." },
                    {
                      pattern: /^[0-9]+$/,
                      message: "Only numbers are allowed.",
                    },
                  ]}
                >
                  <Input
                    placeholder="उदा. 123"
                    size={compact ? "middle" : "large"}
                    onKeyPress={(event) => {
                      if (!/^[0-9]$/.test(event.key)) {
                        event.preventDefault();
                      }
                    }}
                    onPaste={(event) => {
                      event.preventDefault();
                    }}
                    disabled={formDisabled}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} lg={fieldColSpan}>
                <Form.Item
                  label={t("registration_date")}
                  name="filingDate"
                  rules={[
                    { required: true, message: "Please select filing date" },
                  ]}
                >
                  <DatePicker
                    style={{ width: "100%" }}
                    size={compact ? "middle" : "large"}
                    format="MM/DD/YYYY"
                    placeholder="mm/dd/yyyy"
                    disabled={formDisabled}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} lg={fieldColSpan}>
                <Form.Item
                  label="अधिकारी नाव"
                  name="officerName"
                  rules={[
                    { required: true, message: "Please enter officer name" },
                  ]}
                >
                  <Input
                    placeholder="अधिकारी नाव टाका"
                    size={compact ? "middle" : "large"}
                    onKeyPress={(e) => {
                      if (!/[A-Za-z\u0900-\u097F\s]/.test(e.key)) {
                        e.preventDefault();
                      }
                    }}
                    disabled={formDisabled}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} lg={fieldColSpan}>
                <Form.Item
                  label="अधिकारी नंबर"
                  name="officerNumber"
                  rules={[
                    {
                      message: "Please enter a valid 10-digit mobile number",
                    },
                    {
                      pattern: /^[0-9]{10}$/,
                      message: "Please enter a valid 10-digit mobile number",
                    },
                  ]}
                >
                  <Input
                    maxLength={10}
                    onKeyPress={(event) => {
                      if (!/^[0-9]$/.test(event.key)) {
                        event.preventDefault();
                      }
                    }}
                    onPaste={(event) => {
                      event.preventDefault();
                    }}
                    placeholder="मोबाईल / Belt No."
                    size={compact ? "middle" : "large"}
                    disabled={formDisabled}
                  />
                </Form.Item>
              </Col>
            </Row>
          </AiSection>
        </div>
        <div style={sectionVisibility(showPhotoInfo)}>
          <AiSection
            step={compact ? null : "2"}
            title="फोटो माहिती"
            compact={compact || sidebar}
          >
            <Row gutter={[isNarrowLayout ? 0 : 24, 12]}>
              <Col xs={24} lg={fieldColSpan}>
                <Form.Item
                  label="फोटो सोर्स"
                  name="photoSource"
                  rules={[
                    { required: true, message: "Please select photo source" },
                  ]}
                >
                  <Select
                    placeholder="Select photo source"
                    size="large"
                    disabled={formDisabled}
                  >
                    {PHOTO_SOURCE_OPTIONS.map((source) => (
                      <Option key={source} value={source}>
                        {source}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} lg={fieldColSpan}>
                <Form.Item
                  label="Remarks / टिप्पणी"
                  name="remarks"
                  rules={[{ required: true, message: "Please add remark" }]}
                >
                  <Input
                    placeholder="Optional remarks"
                    size="large"
                    disabled={formDisabled}
                    onKeyPress={(e) => {
                      if (!/[A-Za-z\u0900-\u097F\s]/.test(e.key)) {
                        e.preventDefault();
                      }
                    }}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Alert
              type="info"
              showIcon
              style={{
                marginBottom: 16,
                borderRadius: 10,
                border: `1px solid ${AI_THEME.cardBorder}`,
              }}
              message="Original photo only"
              description={`Upload original photos only. Allowed formats: ${ALLOWED_FORMATS_LABEL}. Maximum ${MAX_PHOTOS} photos. Cropping is optional.`}
            />

            <Form.Item
              label="अपलोड फोटो"
              required
              style={{ marginBottom: 0 }}
              {...fullWidthLayout}
            >
              <div
                style={{
                  border: `2px dashed ${AI_THEME.cardBorder}`,
                  borderRadius: 12,
                  padding: compact ? "16px 12px" : "22px 18px",
                  background: "#fafafa",
                  textAlign: "center",
                }}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".png,.jpg,.jpeg,image/png,image/jpeg"
                  style={{ display: "none" }}
                  onChange={handleFileChange}
                />
                <input
                  ref={cropFileInputRef}
                  type="file"
                  accept=".png,.jpg,.jpeg,image/png,image/jpeg"
                  style={{ display: "none" }}
                  onChange={handleCropFileChange}
                />
                <CloudUploadOutlined
                  style={{
                    fontSize: 34,
                    color: AI_THEME.primary,
                    marginBottom: 8,
                  }}
                />
                <div
                  style={{
                    color: AI_THEME.text,
                    fontWeight: 600,
                    marginBottom: 4,
                  }}
                >
                  Drop suspect photo for AI analysis
                </div>
                <div
                  style={{
                    color: AI_THEME.muted,
                    fontSize: 12,
                    marginBottom: 14,
                  }}
                >
                  {ALLOWED_FORMATS_LABEL} only | Max {MAX_PHOTOS} photos
                </div>
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 10,
                    justifyContent: "center",
                  }}
                >
                  <Button
                    type="primary"
                    icon={<CloudUploadOutlined />}
                    onClick={handleChooseFiles}
                    disabled={formDisabled}
                    style={aiPrimaryButtonStyle}
                  >
                    Upload Photo
                  </Button>
                  <Button
                    icon={<ScissorOutlined />}
                    onClick={handleChooseCropFile}
                    disabled={formDisabled}
                    style={aiSecondaryButtonStyle}
                  >
                    Crop & Upload
                  </Button>
                </div>

                {photoList.length > 0 && (
                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 14,
                      marginTop: 22,
                      justifyContent: "center",
                    }}
                  >
                    {photoList.map((photo) => (
                      <div
                        key={photo.uid}
                        style={{
                          position: "relative",
                          width: 108,
                          textAlign: "center",
                        }}
                      >
                        <button
                          type="button"
                          onClick={() =>
                            !formDisabled && handleRemovePhoto(photo.uid)
                          }
                          disabled={formDisabled}
                          style={{
                            position: "absolute",
                            top: -8,
                            right: -8,
                            width: 24,
                            height: 24,
                            borderRadius: "50%",
                            border: "none",
                            background: "#ff4d4f",
                            color: "#fff",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            padding: 0,
                            zIndex: 1,
                          }}
                        >
                          <CloseOutlined style={{ fontSize: 10 }} />
                        </button>
                        <img
                          src={photo.preview}
                          alt={photo.name}
                          style={{
                            width: 100,
                            height: 100,
                            objectFit: "cover",
                            borderRadius: 10,
                            border: `2px solid ${AI_THEME.cardBorder}`,
                            boxShadow: "0 6px 14px rgba(31, 63, 127, 0.1)",
                          }}
                        />
                        <div
                          style={{
                            marginTop: 6,
                            fontSize: 11,
                            color: AI_THEME.muted,
                            wordBreak: "break-all",
                          }}
                        >
                          {photo.name}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </Form.Item>

            <FacePhotoCropModal
              open={cropModalOpen}
              imageSrc={cropImageSrc}
              fileName={pendingCropFile?.name || "photo.jpg"}
              mimeType={pendingCropFile?.type || "image/jpeg"}
              onApply={handleCropApply}
              onUseOriginal={handleUseOriginalPhoto}
              onCancel={closeCropModal}
            />
          </AiSection>
        </div>

        <div style={sectionVisibility(showAccuracy)}>
          <AiSection
            step={compact ? null : "3"}
            title="Search Accuracy"
            compact={compact || sidebar}
          >
            <Form.Item
              label="अ‍ॅक्युरेसी"
              required
              style={{ marginBottom: 12 }}
              {...fullWidthLayout}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 16,
                  width: "100%",
                }}
              >
                <Slider
                  min={0}
                  max={100}
                  value={accuracy}
                  onChange={setAccuracy}
                  disabled={formDisabled}
                  style={{ flex: 1 }}
                  trackStyle={{ background: AI_THEME.primaryBg }}
                  handleStyle={{ borderColor: AI_THEME.primary }}
                />
                <span
                  style={{
                    minWidth: 52,
                    textAlign: "center",
                    fontWeight: 600,
                    color: AI_THEME.primary,
                    background: AI_THEME.primaryBg,
                    borderRadius: 6,
                    padding: "4px 10px",
                  }}
                >
                  {accuracy}%
                </span>
              </div>
            </Form.Item>

            <Form.Item
              name="photoConfirmation"
              valuePropName="checked"
              style={{ marginBottom: 0 }}
              wrapperCol={
                isNarrowLayout
                  ? { span: 24 }
                  : {
                      xs: { span: 24 },
                      sm: { offset: 5, span: 19 },
                      md: { offset: 4, span: 20 },
                      lg: { offset: 3, span: 21 },
                    }
              }
              rules={[
                {
                  validator: (_, value) =>
                    value
                      ? Promise.resolve()
                      : Promise.reject(
                          new Error("Please confirm photo authenticity"),
                        ),
                },
              ]}
            >
              <Checkbox disabled={formDisabled}>
                मी खात्री देतो की अपलोड केलेले फोटो original आहेत आणि AI edited
                / morphed / filtered नाहीत.
              </Checkbox>
            </Form.Item>
          </AiSection>
        </div>

        <div
          style={sectionVisibility(
            showCrimeInfo || showOfficerInfo || showPhotoInfo || showAccuracy,
          )}
        >
          <div
            className={sidebar ? "face-search-form-actions" : undefined}
            style={{
              display: "flex",
              flexDirection: compact ? "column" : "row",
              justifyContent: sidebar ? "stretch" : "center",
              gap: 12,
              marginTop: sidebar ? 4 : 8,
            }}
          >
            <Button
              type="primary"
              htmlType="submit"
              icon={<SearchOutlined />}
              loading={loading}
              disabled={loading}
              block={compact}
              style={
                compact
                  ? { height: 40 }
                  : sidebar
                    ? undefined
                    : aiPrimaryButtonStyle
              }
            >
              Run AI Face Search
            </Button>
            <Button
              icon={<ReloadOutlined />}
              onClick={handleReset}
              disabled={loading}
              block={compact}
              style={
                compact
                  ? { height: 40 }
                  : sidebar
                    ? undefined
                    : aiSecondaryButtonStyle
              }
            >
              Reset
            </Button>
          </div>
        </div>
      </Form>
    </div>
  );
};

const connectedFindFaceSearchForm = connect()(FindFaceSearchFormComponent);
export { connectedFindFaceSearchForm as FindFaceSearchForm };
